import { UserContext } from '@/contexts/UserContext';
import { getAllTargetAttributes } from '@/generated/client/targetattributes/targetattributes';
import { MinimalTarget, TargetAttributeDto, TargetAttributeDtoType } from '@/generated/model';
import { fulldate } from '@/utilities/dateUtils';
import type { i18n as I18nInstance, TFunction } from 'i18next';

export async function targetAttributesExport({
  selectedTargets,
  userCtx,
  t,
  i18n,
}: {
  selectedTargets: MinimalTarget[] | undefined;
  userCtx: UserContext | undefined;
  t: TFunction;
  i18n: I18nInstance;
}) {
  if (!selectedTargets) {
    return;
  }
  const selectedTargetIds = selectedTargets.map((target) => target.id);
  const allTargetAttributes = (await getAllTargetAttributes(selectedTargetIds)).data;
  allTargetAttributes.sort((a, b) => a.target.name.localeCompare(b.target.name));

  const exportColumns = Array.from(
    new Map(
      allTargetAttributes.flatMap((allAttributes) =>
        allAttributes.attributes.map((attr) => [
          attr.key,
          {
            key: attr.key,
            title: getTargetAttributeTitle(attr, t, i18n),
          },
        ])
      )
    ).values()
  );
  let header = [t('targetAttributes.export.name'), ...exportColumns.map((column) => column.title)];
  const isAdmin = userCtx && userCtx.user.isAnyAdmin;
  if (isAdmin) {
    header = ['Id', t('targetAttributes.export.name'), ...exportColumns.map((column) => column.title)];
  }
  const rows = allTargetAttributes.map((allAttributes) => {
    const attrMap = Object.fromEntries(
      allAttributes.attributes.map((targetAttributes) => [targetAttributes.key, targetAttributes])
    );
    let mandatoryValues: Array<string | number> = [allAttributes.target.name];
    if (isAdmin) {
      mandatoryValues = [allAttributes.target.id, allAttributes.target.name];
    }
    return [
      ...mandatoryValues,
      ...exportColumns.map((column) => {
        const val = attrMap[column.key];
        return formatExportValue(val, t);
      }),
    ];
  });

  const csv = [header, ...rows]
    .map((row) => row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = getExportFilename(selectedTargets, t) + '.csv';
  link.click();

  URL.revokeObjectURL(url);
}

function getExportFilename(selectedTargets: MinimalTarget[], t: TFunction) {
  if (selectedTargets && selectedTargets.length === 1) {
    return selectedTargets[0].name;
  } else {
    return t('targetAttributes.exportFileName');
  }
}

function getTargetAttributeTitle(attr: TargetAttributeDto, t: TFunction, i18n: I18nInstance) {
  const translationKey = `targetAttributes.keys.${attr.key}`;
  return i18n.exists(translationKey) ? t(translationKey) : (attr.translatedKey ?? attr.key);
}

function formatExportValue(row: TargetAttributeDto | undefined, t: TFunction) {
  if (!row || row.value == null || row.value == undefined) {
    return '';
  }
  const type: TargetAttributeDtoType = row.type;
  switch (type) {
    case TargetAttributeDtoType['@list']: {
      return row.value.translation;
    }
    case TargetAttributeDtoType['@class']: {
      return row.value.brand + ' ' + row.value.type;
    }
    case TargetAttributeDtoType['@date']: {
      return fulldate(Number(row.value) * 1000, t);
    }
    default:
      return row.value;
  }
}
